import { Component, ViewChild } from "@angular/core";
import { FileSharer } from "@byteowls/capacitor-filesharer";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";

import { BaseComponent } from "../../../base/components/base/base-component";
import { AsyncHelper } from "../../../base/helper/async-helper";
import { BluetoothService } from "../../../base/services/ble/bluetooth.service";
import { CsvExportSettingsComponent } from "../../components/csv-export-settings/csv-export-settings.component";
import { MeasurementTableComponent } from "../../components/measurement-table/measurement-table.component";
import { Measurement } from "../../datamodel/measurement";
import { CsvSettings } from "../../entities/csv/csv-settings";
import { UiHelper } from "../../helpers/ui-helper";
import { CsvExporterService } from "../../services/csv-exporter/csv-exporter.service";
import { DialogService } from "../../services/dialog/dialog.service";
import { QuickModePdf } from "../../services/pdf/documents/quickmode.pdf";
import { QuickMeasurementsService } from "../../services/quick-measurements/quick-measurements.service";
import { SettingsService } from "../../services/settings/settings-service";

/**
 * QuickModeComponent allows the user to do measurements with a bluetooth device without having to create or select a JobTemplate.
 */
@Component({
    selector: "app-quick-mode",
    templateUrl: "./quick-mode.component.html",
    styleUrls: ["./quick-mode.component.scss"]
})
export class QuickModeComponent extends BaseComponent {
    constructor(
        private readonly bluetoothService: BluetoothService,
        private readonly quickModePDF: QuickModePdf,
        private readonly dialogService: DialogService,
        private readonly csvExporterService: CsvExporterService,
        private readonly settingsService: SettingsService,
        private readonly quickMeasurementsService: QuickMeasurementsService,
        private readonly translateService: TranslateService
    ) {
        super();
        this.caption = "QuickMode.title";
    }

    protected exportingPdf: boolean = false;
    protected exportingCsv: boolean = false;

    public measurements: Array<Measurement> = [];
    @ViewChild("measurementTableComponent")
    private measurementTableComponent?: MeasurementTableComponent;
    private onDataSubscription?: Subscription;
    private onDeleteMeasurementSubscription?: Subscription;

    protected componentInit(): void {
        this.onDataSubscription = this.bluetoothService.onData.subscribe((measurement: Measurement) => {
            this.newMeasurement(measurement).then();
        });

        this.loadMeasurements().then();
    }

    protected componentDestroy(): void {
        this.onDataSubscription?.unsubscribe();
        this.onDeleteMeasurementSubscription?.unsubscribe();
    }

    protected override componentEnter(): void {
        this.measurementTableComponent?.refresh();
    }

    protected override componentLeave(): void {
        // Do nothing
    }

    private async loadMeasurements(): Promise<void> {
        this.measurements = await this.quickMeasurementsService.listMeasurements();
        this.measurementTableComponent?.refresh();
    }

    private async newMeasurement(measurement: Measurement): Promise<void> {
        if (!this.componentIsActive) {
            return;
        }

        if (measurement.iteration > 1) {
            const existingMeasurement: Measurement|undefined = this.measurements.find((m: Measurement) => m.id == measurement.id);
            if (existingMeasurement) {
                await this.deleteMeasurement(existingMeasurement);
            }
        }

        let maxIndex: number = 0;
        this.measurements.forEach((m: Measurement) => { maxIndex = Math.max(maxIndex, isNaN(m.localId) || m.localId <= 0 ? 0 : m.localId); });
        measurement.localId = maxIndex + 1;

        this.measurements.unshift(measurement);
        await this.quickMeasurementsService.add(measurement).then();

        this.measurementTableComponent?.refresh();
    }

    public async exportCsv(): Promise<void> {
        let settings: CsvSettings|undefined = (await this.settingsService.loadSettings()).csvSettings ?? new CsvSettings();
        settings.sourceMeasurements = this.measurements;
        settings.columns = [...(UiHelper.measurementsToColumnNames(this.measurements))];

        settings = await this.dialogService.openDialog<CsvSettings>(CsvExportSettingsComponent, { fullscreen: false }, { csvSettings: settings });
        if (settings == null) {
            return;
        }

        try {
            this.exportingCsv = true;
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            await AsyncHelper.sleep(10);
            await this.csvExporterService.export(settings);
        } finally {
            this.exportingCsv = false;
        }
    }

    public async exportPdf(): Promise<void> {
        this.exportingPdf = true;
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        await AsyncHelper.sleep(10);

        try {
            await this.quickModePDF.generatePDF(this.measurements, async (base64Data: string) => {
                try {
                    await FileSharer.share({
                        filename: "measurements.pdf",
                        contentType: "application/pdf",
                        base64Data: base64Data
                    });
                } catch (error) {
                    console.error(error);
                } finally {
                    this.exportingPdf = false;
                }
            });
        } catch (error) {
            console.error(error);
            this.exportingPdf = false;
        }
    }

    public async deleteAllMeasurements(): Promise<void> {
        const confirm: boolean = await this.dialogService.confirm("QuickMode.deleteAllMeasurements", "QuickMode.deleteAllMeasurementsMessage");
        if (!confirm) {
            return;
        }
        await this.quickMeasurementsService.deleteAll();
        this.measurements = [];
    }

    public async deleteMeasurement(measurement: Measurement): Promise<void> {
        await this.quickMeasurementsService.delete(measurement.id);
        this.measurements = this.measurements.filter((m: Measurement) => m.id != measurement.id);
    }

    public async measurementUpdated(measurement: Measurement): Promise<void> {
        await this.quickMeasurementsService.add(measurement);
    }
}
